import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './GestaoAvaliadores.css';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const EMPTY_FORM = { username: '', first_name: '', last_name: '', email: '', password: '', is_staff: false };

function GestaoAvaliadores() {
  const { user } = useAuth();
  const [avaliadores, setAvaliadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingUser, setEditingUser] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [showSenhas, setShowSenhas] = useState(false);
  const [dadosSenhas, setDadosSenhas] = useState(null);
  const [loadingSenhas, setLoadingSenhas] = useState(false);

  const csrfHeaders = { headers: { 'X-CSRFToken': getCookie('csrftoken') } };

  const handleVerSenhas = async () => {
    if (showSenhas) { setShowSenhas(false); return; }
    setLoadingSenhas(true);
    try {
      const res = await axios.get('/api/usuarios-senhas/');
      setDadosSenhas(res.data);
      setShowSenhas(true);
    } catch {
      toast.error('Erro ao carregar lista de senhas.');
    } finally {
      setLoadingSenhas(false);
    }
  };

  const handleExportarExcel = async () => {
    if (!dadosSenhas) return;
    try {
      const wb = XLSX.utils.book_new();

      const grupos = [
        { chave: 'superusers',       label: 'Administrador Geral' },
        { chave: 'coordenadores',    label: 'Coordenadores'       },
        { chave: 'administradores',  label: 'Administradores'     },
        { chave: 'avaliadores',      label: 'Avaliadores'         },
      ];

      const linhas = [];
      linhas.push(['LISTA DE USUÁRIOS E SENHAS — CBACV 2026']);
      linhas.push([`Gerado em: ${new Date().toLocaleString('pt-BR')}`]);
      linhas.push([]);

      grupos.forEach(({ chave, label }) => {
        const lista = dadosSenhas[chave] || [];
        if (!lista.length) return;
        linhas.push([`▶ ${label.toUpperCase()}`]);
        linhas.push(['Usuário', 'Nome Completo', 'E-mail', 'Senha', 'Ativo']);
        lista.forEach(u => {
          linhas.push([
            u.username,
            u.nome_completo,
            u.email || '—',
            u.senha_temporaria || '(não registrada)',
            u.ativo ? 'Sim' : 'Não',
          ]);
        });
        linhas.push([]);
      });

      const ws = XLSX.utils.aoa_to_sheet(linhas);
      ws['!cols'] = [{ wch: 20 }, { wch: 28 }, { wch: 30 }, { wch: 18 }, { wch: 8 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Usuários e Senhas');
      XLSX.writeFile(wb, 'usuarios_senhas_cbacv2026.xlsx');
      toast.success('Planilha exportada com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar planilha.');
      console.error(err);
    }
  };

  const handleImportarPremio = async (e) => {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    setImportando(true);
    setResultadoImport(null);
    try {
      const res = await axios.post('/api/importar-avaliadores-premio/', formData, {
        headers: { 'X-CSRFToken': getCookie('csrftoken'), 'Content-Type': 'multipart/form-data' }
      });
      setResultadoImport(res.data);
      if (res.data.criados > 0) toast.success(res.data.mensagem);
      if (res.data.erros?.length > 0) toast.warning(`${res.data.erros.length} aviso(s) na importação.`);
      fetchAvaliadores();
    } catch (err) {
      toast.error('Erro ao importar planilha.');
    } finally {
      setImportando(false);
      e.target.value = '';
    }
  };

  const temCoordenador = (userId) => {
    const u = avaliadores.find(a => a.id === userId);
    return u ? !!u.is_coordenador : false;
  };
  const temAdminPremio = (userId) => {
    const u = avaliadores.find(a => a.id === userId);
    return u ? !!u.is_admin_premio : false;
  };

  // Atualiza somente funções administrativas. O vínculo de avaliação é feito na sessão.
  const toggleGrupo = async (userId, tipo, ativar) => {
    const campo = tipo === 'coordenador' ? 'is_coordenador'
                : 'is_admin_premio';
    const outroCampo = tipo === 'coordenador' ? 'is_admin_premio'
                      : 'is_coordenador';
    const label = tipo === 'coordenador' ? 'Coordenador'
                : 'Admin de Avaliação';
    try {
      await axios.put(
        `/api/usuarios/${userId}/grupos/`,
        ativar ? { [campo]: true, [outroCampo]: false } : { [campo]: false },
        csrfHeaders
      );
      toast.success(ativar ? `Função definida como ${label}.` : `Função ${label} removida.`);
      fetchAvaliadores();
    } catch (err) {
      toast.error('Erro ao atualizar grupo do avaliador.');
    }
  };

  const fetchAvaliadores = () => {
    setLoading(true);
    axios.get('/api/usuarios/')
      .then(r => { setAvaliadores(r.data); setLoading(false); })
      .catch(() => { toast.error('Erro ao carregar avaliadores.'); setLoading(false); });
  };

  useEffect(() => { fetchAvaliadores(); }, []);

  const isCoord = (u) => u?.is_coordenador || u?.is_superuser;

  if (!isCoord(user)) {
    return (
      <div className="ga-container">
        <div className="ga-access-denied">
          <span className="ga-denied-icon">🔒</span>
          <h2>Acesso Restrito</h2>
          <p>Apenas o administrador geral (Nasser) pode gerenciar avaliadores.</p>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    axios.post('/api/usuarios/', formData, csrfHeaders)
      .then(() => {
        toast.success(`Avaliador "${formData.username}" criado com sucesso!`);
        setFormData(EMPTY_FORM);
        setShowForm(false);
        fetchAvaliadores();
      })
      .catch(err => {
        const msg = err.response?.data?.username?.[0] || 'Erro ao criar avaliador.';
        toast.error(msg);
      });
  };

  const handleUpdate = (userId, changes) => {
    axios.put(`/api/usuarios/${userId}/`, changes, csrfHeaders)
      .then(() => { toast.success('Avaliador atualizado!'); fetchAvaliadores(); setEditingUser(null); })
      .catch(() => toast.error('Erro ao atualizar avaliador.'));
  };

  const handleResetPassword = () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('A senha deve ter ao menos 6 caracteres.'); return;
    }
    axios.post(`/api/usuarios/${resetTarget.id}/reset-senha/`, { new_password: newPassword }, csrfHeaders)
      .then(() => { toast.success(`Senha de "${resetTarget.username}" redefinida!`); setResetTarget(null); setNewPassword(''); })
      .catch(err => toast.error(err.response?.data?.detail || 'Erro ao redefinir senha.'));
  };

  const handleDelete = (u) => {
    axios.delete(`/api/usuarios/${u.id}/`, csrfHeaders)
      .then(() => { toast.success(`Avaliador "${u.username}" removido.`); setConfirmDelete(null); fetchAvaliadores(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Erro ao remover avaliador.'));
  };

  const filtered = avaliadores.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.first_name + ' ' + u.last_name).toLowerCase().includes(search.toLowerCase())
  );

  const superusers   = filtered.filter(u => u.is_superuser);
  const coordenadores = filtered.filter(u => !u.is_superuser && u.is_coordenador);
  const comuns        = filtered.filter(u => !u.is_superuser && !u.is_coordenador);

  return (
    <div className="ga-container">
      <div className="ga-header">
        <div className="ga-header-identity">
          <span className="ga-header-icon">♟</span>
          <div>
          <span className="ga-header-eyebrow">CBACV 2026 · Gestão de pessoas</span>
          <h1 className="ga-title">Gestão de Avaliadores</h1>
          <p className="ga-subtitle">{avaliadores.length} usuário(s) cadastrado(s) · acesso exclusivo do administrador geral</p>
          </div>
        </div>
        <div className="ga-header-actions">
          {user?.is_superuser && (
            <button
              className="ga-btn-senhas"
              onClick={handleVerSenhas}
              disabled={loadingSenhas}
              title="Listar todos os usuários com senhas temporárias"
            >
              {loadingSenhas ? '⏳ Carregando...' : showSenhas ? '🔒 Ocultar Senhas' : '🔑 Ver Senhas'}
            </button>
          )}
          <label className="ga-btn-import" title="Importar cadastros de avaliadores via Excel">
            {importando ? '⏳ Importando...' : '📥 Importar Avaliadores (Excel)'}
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={handleImportarPremio}
              disabled={importando}
            />
          </label>
          <button className="ga-btn-new" onClick={() => { setFormData({ ...EMPTY_FORM }); setShowForm(true); }}>
            + Novo Avaliador
          </button>
        </div>
      </div>

      {/* Resultado da importação */}
      {resultadoImport && (
        <div className="ga-import-result">
          <div className="ga-import-result-header">
            <strong>📊 Resultado da Importação</strong>
            <button onClick={() => setResultadoImport(null)} className="ga-import-close">✕</button>
          </div>
          <p>✅ <strong>{resultadoImport.criados}</strong> avaliador(es) cadastrado(s). Vincule-os às sessões no Painel de Administração.</p>
          {resultadoImport.usuarios_criados?.length > 0 && (
            <p style={{ fontSize: '13px', color: '#555' }}>
              Usuários: {resultadoImport.usuarios_criados.join(', ')}
            </p>
          )}
          {resultadoImport.erros?.length > 0 && (
            <div className="ga-import-erros">
              <strong>⚠️ Avisos:</strong>
              <ul>{resultadoImport.erros.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {/* Painel de Senhas */}
      {showSenhas && dadosSenhas && (
        <div className="ga-senhas-painel">
          <div className="ga-senhas-header">
            <div>
              <strong>🔑 Lista de Usuários e Senhas Temporárias</strong>
              <span className="ga-senhas-aviso"> · Mantenha esta informação em sigilo</span>
            </div>
            <button className="ga-btn-exportar" onClick={handleExportarExcel}>
              📊 Exportar para Excel
            </button>
          </div>

          {[
            { chave: 'superusers',       emoji: '👑', label: 'Administrador Geral'  },
            { chave: 'coordenadores',    emoji: '🏅', label: 'Coordenadores'        },
            { chave: 'administradores',  emoji: '🛡️', label: 'Administradores'      },
            { chave: 'avaliadores',      emoji: '👤', label: 'Avaliadores'          },
          ].map(({ chave, emoji, label }) => {
            const lista = dadosSenhas[chave] || [];
            if (!lista.length) return null;
            return (
              <div key={chave} className="ga-senhas-grupo">
                <h3 className="ga-senhas-grupo-titulo">{emoji} {label} <span className="ga-senhas-count">({lista.length})</span></h3>
                <table className="ga-senhas-tabela">
                  <thead>
                    <tr>
                      <th>Usuário</th>
                      <th>Nome Completo</th>
                      <th>E-mail</th>
                      <th>Senha Temporária</th>
                      <th>Ativo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map(u => (
                      <tr key={u.id}>
                        <td><code>@{u.username}</code></td>
                        <td>{u.nome_completo}</td>
                        <td>{u.email || <span className="ga-sem-info">—</span>}</td>
                        <td>
                          {u.senha_temporaria
                            ? <code className="ga-senha-valor">{u.senha_temporaria}</code>
                            : <span className="ga-sem-info">não registrada</span>
                          }
                        </td>
                        <td>{u.ativo ? '✅' : '❌'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* Busca */}
      <div className="ga-search-bar">
        <span className="ga-search-icon">🔍</span>
        <input
          type="text"
          placeholder="Buscar por nome ou usuário…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="ga-search-input"
        />
        {search && <button className="ga-search-clear" onClick={() => setSearch('')}>✕</button>}
      </div>

      {/* Modal: Criar Avaliador */}
      {showForm && (
        <div className="ga-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-header">
              <h2>Novo Avaliador</h2>
              <button className="ga-modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate} className="ga-form" autoComplete="off">
              <div className="ga-form-row">
                <div className="ga-field">
                  <label>Usuário *</label>
                  <input
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="login"
                    autoComplete="off"
                    required
                    autoFocus
                  />
                </div>
                <div className="ga-field">
                  <label>Senha *</label>
                  <input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="mín. 6 caracteres"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>
              <div className="ga-form-row">
                <div className="ga-field">
                  <label>Nome</label>
                  <input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Primeiro nome" autoComplete="off" />
                </div>
                <div className="ga-field">
                  <label>Sobrenome</label>
                  <input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Sobrenome" autoComplete="off" />
                </div>
              </div>
              <div className="ga-field">
                <label>E-mail</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="email@exemplo.com" autoComplete="off" />
              </div>
              <label className="ga-checkbox-label">
                <input type="checkbox" name="is_staff" checked={formData.is_staff} onChange={handleChange} />
                <span className="ga-checkbox-custom"></span>
                Conceder acesso de administrador (painel admin + gestão de sessões)
              </label>
              <div className="ga-form-actions">
                <button type="submit" className="ga-btn-primary">✓ Criar Avaliador</button>
                <button type="button" className="ga-btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Editar Avaliador */}
      {editingUser && (
        <div className="ga-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-header">
              <h2>Editar: <span className="ga-modal-username">@{editingUser.username}</span></h2>
              <button className="ga-modal-close" onClick={() => setEditingUser(null)}>✕</button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleUpdate(editingUser.id, {
              first_name: editingUser.first_name, last_name: editingUser.last_name,
              email: editingUser.email, is_staff: editingUser.is_staff, is_active: editingUser.is_active
            }); }} className="ga-form">
              <div className="ga-form-row">
                <div className="ga-field">
                  <label>Nome</label>
                  <input value={editingUser.first_name} onChange={e => setEditingUser(p => ({...p, first_name: e.target.value}))} placeholder="Primeiro nome" />
                </div>
                <div className="ga-field">
                  <label>Sobrenome</label>
                  <input value={editingUser.last_name} onChange={e => setEditingUser(p => ({...p, last_name: e.target.value}))} placeholder="Sobrenome" />
                </div>
              </div>
              <div className="ga-field">
                <label>E-mail</label>
                <input type="email" value={editingUser.email} onChange={e => setEditingUser(p => ({...p, email: e.target.value}))} placeholder="email@exemplo.com" />
              </div>
              <div className="ga-toggles">
                <label className="ga-checkbox-label">
                  <input type="checkbox" checked={editingUser.is_staff} disabled={editingUser.is_superuser}
                    onChange={e => setEditingUser(p => ({...p, is_staff: e.target.checked}))} />
                  <span className="ga-checkbox-custom"></span>
                  Administrador (acesso ao painel admin)
                </label>
                <label className="ga-checkbox-label">
                  <input type="checkbox" checked={editingUser.is_active} disabled={editingUser.is_superuser}
                    onChange={e => setEditingUser(p => ({...p, is_active: e.target.checked}))} />
                  <span className="ga-checkbox-custom"></span>
                  Conta ativa (pode fazer login)
                </label>
              </div>
              {editingUser.is_superuser && (
                <p className="ga-superuser-note">⭐ Superusuário — permissões não podem ser alteradas aqui.</p>
              )}
              <div className="ga-form-actions">
                <button type="submit" className="ga-btn-primary">✓ Salvar Alterações</button>
                <button type="button" className="ga-btn-ghost" onClick={() => setEditingUser(null)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Redefinir Senha */}
      {resetTarget && (
        <div className="ga-modal-overlay" onClick={() => setResetTarget(null)}>
          <div className="ga-modal ga-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-header">
              <h2>🔑 Redefinir Senha</h2>
              <button className="ga-modal-close" onClick={() => setResetTarget(null)}>✕</button>
            </div>
            <p className="ga-modal-desc">Definir nova senha para <strong>@{resetTarget.username}</strong>:</p>
            <div className="ga-field">
              <label>Nova Senha (mín. 6 caracteres)</label>
              <div className="ga-password-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Nova senha"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                />
                <button
                  type="button"
                  className="ga-toggle-password"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                  title={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showNewPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="ga-form-actions">
              <button className="ga-btn-primary" onClick={handleResetPassword}>✓ Redefinir</button>
              <button className="ga-btn-ghost" onClick={() => { setResetTarget(null); setNewPassword(''); setShowNewPassword(false); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Confirmar Exclusão */}
      {confirmDelete && (
        <div className="ga-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="ga-modal ga-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-header ga-modal-danger">
              <h2>⚠️ Confirmar Exclusão</h2>
              <button className="ga-modal-close" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <p className="ga-modal-desc">
              Tem certeza que deseja remover o avaliador <strong>@{confirmDelete.username}</strong>?
              <br /><span className="ga-warn-text">Esta ação não pode ser desfeita.</span>
            </p>
            <div className="ga-form-actions">
              <button className="ga-btn-danger" onClick={() => handleDelete(confirmDelete)}>🗑 Remover</button>
              <button className="ga-btn-ghost" onClick={() => setConfirmDelete(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div className="ga-loading">Carregando avaliadores…</div>
      ) : (
        <>
          {superusers.length > 0 && (
            <UserTable title="👑 Administrador Geral" users={superusers}
              onEdit={setEditingUser} onReset={setResetTarget} onDelete={setConfirmDelete}
              currentUserId={user.id}
              temCoord={temCoordenador}
              temAdminPremio={temAdminPremio}
              onToggleGrupo={toggleGrupo} isSuperuser={user.is_superuser} />
          )}
          {coordenadores.length > 0 && (
            <UserTable title="🗂️ Coordenadores" users={coordenadores}
              onEdit={setEditingUser} onReset={setResetTarget} onDelete={setConfirmDelete}
              currentUserId={user.id}
              temCoord={temCoordenador}
              temAdminPremio={temAdminPremio}
              onToggleGrupo={toggleGrupo} isSuperuser={user.is_superuser} />
          )}
          {comuns.length > 0 && (
            <UserTable title="📋 Avaliadores" users={comuns}
              onEdit={setEditingUser} onReset={setResetTarget} onDelete={setConfirmDelete}
              currentUserId={user.id}
              temCoord={temCoordenador}
              temAdminPremio={temAdminPremio}
              onToggleGrupo={toggleGrupo} isSuperuser={user.is_superuser} />
          )}
          {filtered.length === 0 && (
            <div className="ga-empty">Nenhum avaliador encontrado.</div>
          )}
        </>
      )}
    </div>
  );
}

function UserTable({ title, users, onEdit, onReset, onDelete, currentUserId, temCoord, temAdminPremio, onToggleGrupo, isSuperuser }) {
  return (
    <div className="ga-section">
      <h3 className="ga-section-title">{title}</h3>
      <div className="ga-table-wrapper">
        <table className="ga-table">
          <thead>
            <tr>
              <th>Usuário / Nome</th>
              <th>Status</th>
              <th style={{textAlign:'center'}}>🗂️ Coord.</th>
              <th style={{textAlign:'center'}}>🎖️ Admin Avaliação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={!u.is_active ? 'ga-row-inactive' : ''}>
                <td>
                  <span className="ga-username">@{u.username}</span>
                  {u.is_superuser && <span className="ga-badge ga-badge-super">SUPER</span>}
                  {u.is_coordenador && !u.is_superuser && <span className="ga-badge ga-badge-coord">COORD</span>}
                  {u.is_admin_premio && !u.is_superuser && !u.is_coordenador && <span className="ga-badge ga-badge-admin-premio">ADM 🏆</span>}
                  {u.id === currentUserId && <span className="ga-badge ga-badge-you">você</span>}
                  <div style={{fontSize:'11px', color:'#9aabbd', marginTop:'2px'}}>
                    {(u.first_name || u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : ''}
                  </div>
                </td>
                <td>
                  <span className={`ga-status ${u.is_active ? 'ga-status-active' : 'ga-status-inactive'}`}>
                    {u.is_active ? '● Ativo' : '○ Inativo'}
                  </span>
                </td>
                <td style={{textAlign:'center'}}>
                  {u.is_superuser ? (
                    <span className="ga-grupo-todos">👑</span>
                  ) : (
                    <label className="ga-grupo-toggle">
                      <input type="checkbox"
                        checked={temCoord ? temCoord(u.id) : false}
                        onChange={e => onToggleGrupo && onToggleGrupo(u.id, 'coordenador', e.target.checked)}
                        disabled={!isSuperuser}
                      />
                      <span className={'ga-grupo-box ga-grupo-box-coord ' + (temCoord && temCoord(u.id) ? 'ga-grupo-on-coord' : '')}>
                        {temCoord && temCoord(u.id) ? '✓' : ''}
                      </span>
                    </label>
                  )}
                </td>
                <td style={{textAlign:'center'}}>
                  {u.is_superuser ? (
                    <span className="ga-grupo-todos">✅</span>
                  ) : (
                    <label className="ga-grupo-toggle">
                      <input type="checkbox"
                        checked={temAdminPremio ? temAdminPremio(u.id) : false}
                        onChange={e => onToggleGrupo && onToggleGrupo(u.id, 'admin_premio', e.target.checked)}
                        disabled={!isSuperuser}
                      />
                      <span className={'ga-grupo-box ga-grupo-box-admin-premio ' + (temAdminPremio && temAdminPremio(u.id) ? 'ga-grupo-on-admin-premio' : '')}>
                        {temAdminPremio && temAdminPremio(u.id) ? '✓' : ''}
                      </span>
                    </label>
                  )}
                </td>
                <td>
                  <div className="ga-actions">
                    {u.is_superuser && !isSuperuser ? (
                      <span className="ga-superuser-note" title="Somente um superusuário pode alterar esta conta">
                        🔒 Protegido
                      </span>
                    ) : (
                      <>
                        <button className="ga-btn-action ga-btn-edit" onClick={() => onEdit({...u})} title="Editar">✏️</button>
                        <button className="ga-btn-action ga-btn-key" onClick={() => onReset(u)} title="Redefinir senha">🔑</button>
                        {!u.is_superuser && u.id !== currentUserId && (
                          <button className="ga-btn-action ga-btn-del" onClick={() => onDelete(u)} title="Remover">🗑</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default GestaoAvaliadores;
